/**
 * How long a video job still takes. While the animals are tracked it is the frames left at the speed measured
 * from the live progress, plus making the result videos; before that, typical times.
 * Measured on demo, 20 Oct 2026 (T4 and L4 machines, reindeer): starting the machine and preparing the video
 * 2-3 min; tracking 0.94-2.2 frames a second (dense herds are slower); result videos and report 1.2-14.1 min, up to
 * 0.029 s per frame and megapixel (a dense 4K herd on a T4; sparse scenes need far less).
 */

export const SETUP_SEC = 150;
// before a speed is measured
export const TYPICAL_FPS = 1.3;
const FINISH_MIN_SEC = 60;
const FINISH_SEC_PER_FRAME_MP = 0.025;
// 4K, until the frame size is known
const DEFAULT_MEGAPIXELS = 8.3;
// a speed needs this much time between two progress updates
const MIN_SPAN_SEC = 15;
const WINDOW_SEC = 120;
// the frames assumed per second of video before the job knows the real count
const ASSUMED_VIDEO_FPS = 30;

/** Making the result videos and the report. */
export const finishSeconds = (frames, megapixels = DEFAULT_MEGAPIXELS) =>
	FINISH_MIN_SEC + frames * megapixels * FINISH_SEC_PER_FRAME_MP;

/** Frames a second from progress samples [{ t (s), done }], over the last WINDOW_SEC; null until measurable. */
export const frameRate = samples => {
	if (!samples?.length) return null;
	const last = samples[samples.length - 1];
	const first = samples.find(s => last.t - s.t <= WINDOW_SEC) || samples[0];
	const span = last.t - first.t;
	if (span < MIN_SPAN_SEC || last.done <= first.done) return null;
	return (last.done - first.done) / span;
};

/**
 * Seconds left. pct: the job's progress (20-69 tracking, 70+ result videos); live: live_progress; rate: frameRate;
 * analysedSeconds: length of the analysed part; elapsedSec: since the job started; megapixels: of a video frame.
 */
export const secondsLeft = ({
	pct = 0,
	live = null,
	rate = null,
	analysedSeconds = 0,
	elapsedSec = 0,
	megapixels = DEFAULT_MEGAPIXELS,
}) => {
	const total = live?.frames_total || Math.round(analysedSeconds * ASSUMED_VIDEO_FPS);
	if (!total) return null;
	const finish = finishSeconds(total, megapixels);
	if (pct >= 70) return Math.max(15, (finish * (100 - pct)) / 30);
	const done = live?.frames_done || 0;
	const tracking = (total - done) / (rate || TYPICAL_FPS);
	const setup = live ? 0 : Math.max(30, SETUP_SEC - elapsedSec);
	return setup + tracking + finish;
};

/** 400 → "about 7 min left" */
export const leftLabel = sec => {
	if (sec == null) return '';
	if (sec < 60) return 'less than a minute left';
	const min = sec < 600 ? Math.round(sec / 60) : Math.round(sec / 300) * 5;
	return `about ${min} min left`;
};
